import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';

export const usePantries = () => {
  const [pantries, setPantries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsubscribeSnapshot = null;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }

      if (!user) {
        setPantries([]);
        setLoading(false);
        return;
      }

      // No orderBy — avoids composite index requirement
      const q = query(
        collection(db, 'pantries'),
        where('userId', '==', user.uid)
      );

      unsubscribeSnapshot = onSnapshot(
        q,
        (snapshot) => {
          const pantriesData = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data()
          }));
          setPantries(pantriesData);
          setError(null);
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching pantries:', err);
          setError(err.message);
          setLoading(false);
        }
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  const createPantry = async (name, description = '') => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const newPantry = {
        name,
        description: description || '',
        userId: auth.currentUser.uid,
        items: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'pantries'), newPantry);
      return { id: docRef.id, ...newPantry, createdAt: new Date(), updatedAt: new Date() };
    } catch (err) {
      console.error('Error creating pantry:', err);
      throw err;
    }
  };

  const addItemToPantry = async (pantryId, product) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const pantry = pantries.find(p => p.id === pantryId);
      if (!pantry) throw new Error('Pantry not found');
      const newItem = {
        productId: product.id,
        name: product.name,
        brand: product.brand,
        imageURL: product.imageURL || product.imageUrl || null,
        category: product.category || 'other',
        dietaryTags: product.dietaryTags || [],
        addedAt: new Date()
      };
      const updatedItems = [...(pantry.items || []), newItem];
      await updateDoc(doc(db, 'pantries', pantryId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding item to pantry:', err);
      throw err;
    }
  };

  const removeItemFromPantry = async (pantryId, productId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const pantry = pantries.find(p => p.id === pantryId);
      if (!pantry) throw new Error('Pantry not found');
      const updatedItems = pantry.items.filter(item => item.productId !== productId);
      await updateDoc(doc(db, 'pantries', pantryId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error removing item from pantry:', err);
      throw err;
    }
  };

  const deletePantry = async (pantryId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await deleteDoc(doc(db, 'pantries', pantryId));
    } catch (err) {
      console.error('Error deleting pantry:', err);
      throw err;
    }
  };

  const renamePantry = async (pantryId, newName) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'pantries', pantryId), {
        name: newName,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error renaming pantry:', err);
      throw err;
    }
  };

  const updateDescription = async (pantryId, description) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'pantries', pantryId), {
        description,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating pantry description:', err);
      throw err;
    }
  };

  const updatePantryCareProfiles = async (pantryId, careProfileIds) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'pantries', pantryId), {
        careProfileIds,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating pantry care profiles:', err);
      throw err;
    }
  };

  return {
    pantries,
    loading,
    error,
    createPantry,
    addItemToPantry,
    removeItemFromPantry,
    deletePantry,
    renamePantry,
    updateDescription,
    updatePantryCareProfiles
  };
};
